import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Send, MessageSquare, Bell, Unlink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Props {
  userId: string;
}

const TelegramLinkCard = ({ userId }: Props) => {
  const [linked, setLinked] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState(false);
  const { toast } = useToast();

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.functions.invoke("telegram-link-code");
      if (data?.linked) {
        setLinked(true);
        setUsername(data.username);
        setCode(null);
      } else {
        setLinked(false);
        setUsername(null);
        setCode(data?.code ?? null);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [userId]);

  // Auto-refresh when bot links the account (realtime + polling fallback)
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`tg-link-${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "user_telegram", filter: `user_id=eq.${userId}` }, () => refresh())
      .subscribe();
    const interval = setInterval(() => { if (!linked) refresh(); }, 5000);
    return () => { supabase.removeChannel(channel); clearInterval(interval); };
  }, [userId, linked]);

  const botUsername = "fitawrarifx_bot";
  const linkUrl = code ? `https://t.me/${botUsername}?start=${code}` : null;

  const copyCode = () => {
    if (code) {
      navigator.clipboard.writeText(`/start ${code}`);
      toast({ title: "Copied!", description: "Paste it in the Telegram bot chat." });
    }
  };

  const unlink = async () => {
    if (!(await (window.confirm as any)("Unlink your Telegram account? You can link a different account afterwards."))) return;
    setUnlinking(true);
    try {
      const { error } = await supabase.from("user_telegram").delete().eq("user_id", userId);
      if (error) throw error;
      toast({ title: "Unlinked", description: "Generate a new code to link a different Telegram account." });
      await refresh();
    } catch (e: any) {
      toast({ title: "Failed", description: e.message ?? "Try again.", variant: "destructive" });
    }
    setUnlinking(false);
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" /> Telegram Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : linked ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Bell className="w-4 h-4 text-primary" />
              <span>Connected{username ? ` as @${username}` : ""}. You'll receive lesson reminders and can ask questions.</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={refresh}>Refresh</Button>
              <Button size="sm" variant="outline" onClick={unlink} disabled={unlinking}>
                <Unlink className="w-4 h-4 mr-1" /> {unlinking ? "Unlinking..." : "Change account"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              To use a different Telegram account, click <b>Change account</b>, then link the new one.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Get streak reminders, lesson updates, and chat with admins via Telegram.
            </p>
            {code && linkUrl && (
              <div className="flex flex-col sm:flex-row gap-2">
                <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button size="sm" className="w-full"><MessageSquare className="w-4 h-4 mr-1" /> Open Telegram Bot</Button>
                </a>
                <Button size="sm" variant="outline" onClick={copyCode}>Copy code</Button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Or message <b>@{botUsername}</b> with: <code>/start {code}</code></p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TelegramLinkCard;
